/**
 * Direction opposites (D244; the Act II specification's §13.3) — a pack's
 * own direction words, declared with the word that goes back.
 *
 * @remarks
 * The engine has always let a pack declare a direction of its own: a word
 * the parser reads as movement, that exits answer to (as their
 * `directionToken` or through `alsoAnswersTo`, D227), and that the map
 * leaves alone unless it is an exit's drawn word. What a pack's own word has
 * never had is a compass to borrow its opposite from — `spinward` is not the
 * reverse of anything the engine knows. `opposite` says it, and two rules
 * read it:
 *
 * - **It must mean something** (a failure): name another declared
 *   direction's canonical token — not an alias, not itself — and that
 *   direction must name this one back. Half a pair is a typo.
 * - **The rooms are held to it** (a warning, D137's channel): an exit that
 *   answers a word with a declared opposite, leading to a room with no exit
 *   that answers the opposite back, is a ring that cannot be walked back the
 *   way it was walked out. It is usually a missed word — and sometimes a
 *   one-way chute on purpose, which is why it is reported and not refused.
 *
 * The base vocabulary declares no opposites (D244's ruling): the compass
 * has a thousand honest one-way exits across the packs already shipped (a
 * doorway that is `in` one way and `south` the other), and holding them to
 * `north`/`south` would report what nobody got wrong. A pack opts its own
 * words in.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition, LexiconDefinition } from '@orpheus/content-schema';

import { type CompiledLexicon, normalizeAuthoredToken } from '@orpheus/parser-engine';

import { brokenReference } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/** One exit that leads away by a word whose opposite does not lead back. */
export interface OneWayDirection {
	readonly exitId: string;
	readonly opposite: string;
	readonly roomId: string;
	readonly targetRoomId: string;
	readonly word: string;
}

/**
 * Each declared canonical direction with an opposite → that opposite, both
 * normalized as the parser normalizes them.
 *
 * @param lexicon - The merged authored vocabulary
 * @returns The declared pairs, one entry per direction that names an opposite
 */
const declaredOppositesOf = (lexicon: LexiconDefinition): ReadonlyMap<string, string> =>
	new Map(
		lexicon.directions.flatMap((direction) =>
			direction.opposite === undefined ? [] : [[normalizeAuthoredToken(direction.canonicalToken), normalizeAuthoredToken(direction.opposite)] as const],
		),
	);

/**
 * Judge every declared opposite against the merged vocabulary.
 *
 * @param lexicon - The merged authored vocabulary (where the opposites are written)
 * @param compiled - The compiled vocabulary (the authority on which directions exist)
 * @returns One failure per opposite that names nothing, an alias, itself, or a direction that does not name it back
 */
export const validateDirectionOpposites = (lexicon: LexiconDefinition, compiled: CompiledLexicon): readonly ReferenceBrokenFailure[] => {
	const opposites = declaredOppositesOf(lexicon);
	const failures: ReferenceBrokenFailure[] = [];

	for (const [direction, opposite] of opposites) {
		const path = `vocabulary.directions(${direction}).opposite`;
		const named = compiled.directionsByToken.get(opposite) as string | undefined;

		if (named === undefined) {
			failures.push(brokenReference(path, opposite, 'no direction by that word is declared, so it can be nobody\'s way back'));
		} else if (named !== opposite) {
			failures.push(brokenReference(path, opposite, `"${opposite}" is an alias of "${named}" — name the canonical direction`));
		} else if (opposite === direction) {
			failures.push(brokenReference(path, opposite, 'a direction cannot be its own way back'));
		} else if (opposites.get(opposite) !== direction) {
			failures.push(brokenReference(path, opposite, `"${opposite}" does not name "${direction}" as its opposite — declare the pair on both directions, or on neither`));
		}
	}

	return failures;
};

/**
 * Every exit that leads away by a word with a declared opposite, to a room
 * that does not lead back by that opposite.
 *
 * @param contentPack - The parsed pack
 * @param lexicon - The merged authored vocabulary
 * @returns The one-way exits, in document order
 */
export const findOneWayDirections = (contentPack: ContentPackDefinition, lexicon: LexiconDefinition): readonly OneWayDirection[] => {
	const opposites = declaredOppositesOf(lexicon);
	const roomsById = new Map(contentPack.rooms.map((room) => [room.roomId as string, room]));

	return contentPack.rooms.flatMap((room) =>
		room.exits.flatMap((exit) => {
			const target = exit.targetRoomId === undefined ? undefined : roomsById.get(exit.targetRoomId);

			if (target === undefined) {
				return [];
			}

			return [exit.directionToken, ...(exit.alsoAnswersTo ?? [])].flatMap((word): readonly OneWayDirection[] => {
				const opposite = opposites.get(word);
				const hasWayBack =
					opposite === undefined ||
					target.exits.some((back) => back.targetRoomId === room.roomId && [back.directionToken, ...(back.alsoAnswersTo ?? [])].includes(opposite));

				return hasWayBack ? [] : [{ exitId: exit.exitId, opposite: opposite, roomId: room.roomId, targetRoomId: target.roomId, word }];
			});
		}),
	);
};
